import React, { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Building2, ChevronDown, ChevronUp, Users, X, ArrowRightLeft, Briefcase, UserMinus, ShieldAlert, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { EmptyState } from "../shared/EmptyState";
import { UserSearchSelect } from "../ui/UserSearchSelect";
import { UserAvatar } from "../shared/UserAvatar";
import { toast } from "sonner";

function isPlaceholderEmail(email) {
    return !email || email.includes("@no-email.local");
}

/* =============== EXCLUSIONS PANEL =============== */
function ExclusionsPanel({ deptId, deptName }) {
    const [exclusions, setExclusions] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadExclusions = useCallback(async () => {
        try {
            const data = await api.getDepartmentExclusions(deptId);
            setExclusions(data);
        } catch { setExclusions([]); }
        setLoading(false);
    }, [deptId]);

    useEffect(() => { loadExclusions(); }, [loadExclusions]);

    const handleReintegrate = async (excl) => {
        if (!window.confirm(`Supprimer l'exclusion de ${excl.user_name} ? La prochaine synchronisation pourra le/la réintégrer.`)) return;
        try {
            await api.deleteDepartmentExclusion(excl.id);
            toast.success(`Exclusion supprimée. La prochaine synchronisation pourra réintégrer ${excl.user_name} dans ${deptName}.`);
            loadExclusions();
        } catch { toast.error("Erreur lors de la suppression de l'exclusion"); }
    };

    if (loading) return <p className="text-xs text-gray-400 py-2">Chargement...</p>;

    return (
        <div className="mt-3 pt-3 border-t border-gray-200" data-testid="exclusions-panel">
            <h4 className="text-[11px] uppercase text-gray-400 tracking-wider mb-2 flex items-center gap-1">
                <ShieldAlert className="w-3 h-3" />
                Exclusions de synchronisation ({exclusions.length})
            </h4>
            {exclusions.length === 0 ? (
                <p className="text-xs text-gray-400 italic">Aucun utilisateur exclu de ce département</p>
            ) : (
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-[10px] uppercase text-gray-400 tracking-wider">
                            <th className="text-left pb-1">Utilisateur</th>
                            <th className="text-left pb-1">Exclu par</th>
                            <th className="text-left pb-1">Date</th>
                            <th className="text-left pb-1">Motif</th>
                            <th className="text-right pb-1"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {exclusions.map((e) => (
                            <tr key={e.id} className="border-t border-gray-100" data-testid={`exclusion-row-${e.id}`}>
                                <td className="py-1.5 font-medium">{e.user_name}</td>
                                <td className="py-1.5 text-gray-500">{e.excluded_by_name}</td>
                                <td className="py-1.5 text-gray-500">{new Date(e.created_at).toLocaleDateString("fr-FR")}</td>
                                <td className="py-1.5 text-gray-500 max-w-[200px] truncate">{e.reason || "—"}</td>
                                <td className="py-1.5 text-right">
                                    <Button variant="ghost" size="sm" className="h-6 text-xs text-indigo-600 hover:text-indigo-800" onClick={() => handleReintegrate(e)} data-testid={`reintegrate-btn-${e.id}`}>
                                        <RotateCcw className="w-3 h-3 mr-1" />Réintégrer
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

/* =============== MEMBERS PANEL =============== */
function MembersPanel({ members, dept, positions, selectedMemberIds, setSelectedMemberIds, bulkDropdown, setBulkDropdown, openTransferDialog, onRefresh, onRemoveMember }) {
    const allSelected = members.length > 0 && members.every((m) => selectedMemberIds.has(m.id));
    const selCount = members.filter((m) => selectedMemberIds.has(m.id)).length;
    const toggleAll = () => {
        if (allSelected) setSelectedMemberIds(new Set());
        else setSelectedMemberIds(new Set(members.map((m) => m.id)));
    };
    const toggle = (id) => {
        setSelectedMemberIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const selectedUsers = members.filter((m) => selectedMemberIds.has(m.id));
    const selectedPositionCounts = {};
    selectedUsers.forEach((u) => { (u.position_ids || []).forEach((pid) => { selectedPositionCounts[pid] = (selectedPositionCounts[pid] || 0) + 1; }); });

    const handleBulkAssign = async (posId) => {
        setBulkDropdown(null);
        const pos = positions.find((p) => p.id === posId);
        if (!pos || !window.confirm(`Attribuer "${pos.name}" à ${selCount} membre(s) ?`)) return;
        try {
            const result = await api.bulkAssignPosition({ user_ids: [...selectedMemberIds], position_id: posId, action: "add" });
            toast.success(`Poste "${pos.name}" attribué à ${result.updated} membre(s)`);
            setSelectedMemberIds(new Set());
            onRefresh();
        } catch { toast.error("Erreur"); }
    };
    const handleBulkRemove = async (posId) => {
        setBulkDropdown(null);
        const pos = positions.find((p) => p.id === posId);
        if (!pos || !window.confirm(`Retirer "${pos.name}" de ${selCount} membre(s) ?`)) return;
        try {
            const result = await api.bulkAssignPosition({ user_ids: [...selectedMemberIds], position_id: posId, action: "remove" });
            toast.success(`Poste "${pos.name}" retiré de ${result.updated} membre(s)`);
            setSelectedMemberIds(new Set());
            onRefresh();
        } catch { toast.error("Erreur"); }
    };

    return (
        <>
            {selCount > 0 && (
                <div className="flex items-center gap-2 mb-3 p-2 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="dept-bulk-bar">
                    <span className="text-xs font-semibold text-indigo-800">{selCount} sélectionné(s)</span>
                    <div className="flex-1" />
                    <div className="relative">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setBulkDropdown(bulkDropdown === "pos-add" ? null : "pos-add")} data-testid="dept-bulk-assign-btn">
                            <Briefcase className="w-3 h-3 mr-1" />Attribuer <ChevronDown className="w-3 h-3 ml-0.5" />
                        </Button>
                        {bulkDropdown === "pos-add" && positions.length > 0 && (
                            <div className="absolute z-50 mt-1 right-0 w-56 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="dept-bulk-pos-add-dropdown">
                                {positions.map((p) => (<button key={p.id} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b last:border-b-0" onClick={() => handleBulkAssign(p.id)}>{p.name}</button>))}
                            </div>
                        )}
                    </div>
                    <div className="relative">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setBulkDropdown(bulkDropdown === "pos-rm" ? null : "pos-rm")} data-testid="dept-bulk-remove-btn">
                            <X className="w-3 h-3 mr-1" />Retirer poste <ChevronDown className="w-3 h-3 ml-0.5" />
                        </Button>
                        {bulkDropdown === "pos-rm" && (
                            <div className="absolute z-50 mt-1 right-0 w-56 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="dept-bulk-pos-rm-dropdown">
                                {Object.keys(selectedPositionCounts).length === 0 ? (
                                    <p className="px-3 py-2 text-xs text-gray-400">Aucun poste à retirer</p>
                                ) : Object.entries(selectedPositionCounts).map(([pid, cnt]) => {
                                    const pos = positions.find((p) => p.id === pid);
                                    return pos ? (
                                        <button key={pid} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b last:border-b-0 flex justify-between" onClick={() => handleBulkRemove(pid)}>
                                            <span>{pos.name}</span><span className="text-gray-400">{cnt}/{selCount}</span>
                                        </button>
                                    ) : null;
                                })}
                            </div>
                        )}
                    </div>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50" onClick={() => onRemoveMember(selectedUsers, dept)} data-testid="dept-bulk-remove-members-btn">
                        <UserMinus className="w-3 h-3 mr-1" />Retirer du département
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelectedMemberIds(new Set())}>Annuler</Button>
                </div>
            )}
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-[11px] uppercase text-gray-400 tracking-wider">
                        <th className="text-left pb-2 w-8"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-members" /></th>
                        <th className="text-left pb-2">Nom</th>
                        <th className="text-left pb-2">Poste(s)</th>
                        <th className="text-left pb-2">Rôle</th>
                        <th className="text-left pb-2">Type</th>
                        <th className="text-left pb-2">Statut</th>
                        <th className="text-right pb-2"></th>
                    </tr>
                </thead>
                <tbody>
                    {members.map((m) => {
                        const posNames = (m.position_ids || []).map((pid) => positions.find((p) => p.id === pid)?.name).filter(Boolean);
                        return (
                            <tr key={m.id} className={`border-t border-gray-200 ${selectedMemberIds.has(m.id) ? "bg-indigo-50/50" : ""}`}>
                                <td className="py-2"><Checkbox checked={selectedMemberIds.has(m.id)} onCheckedChange={() => toggle(m.id)} data-testid={`select-member-${m.id}`} /></td>
                                <td className="py-2">
                                    <div className="flex items-center gap-2">
                                        <UserAvatar user={m} size="sm" />
                                        <span>{m.first_name} {m.last_name}</span>
                                    </div>
                                </td>
                                <td>
                                    {posNames.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">{posNames.map((n) => <span key={n} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700">{n}</span>)}</div>
                                    ) : <span className="text-gray-300 text-xs">—</span>}
                                </td>
                                <td><span className="text-xs font-medium capitalize">{m.role}</span></td>
                                <td>
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${m.membership_type === "primary" ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-600"}`} data-testid={`member-type-${m.id}`}>
                                        {m.membership_type === "primary" ? "Principal" : "Secondaire"}
                                    </span>
                                </td>
                                <td>
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${m.is_active !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                        {m.is_active !== false ? "Actif" : "Inactif"}
                                    </span>
                                </td>
                                <td className="text-right">
                                    <div className="flex items-center gap-1 justify-end">
                                        <Button variant="ghost" size="sm" onClick={() => onRemoveMember([m], dept)} title="Retirer du département" data-testid={`remove-member-${m.id}`}>
                                            <UserMinus className="w-3.5 h-3.5 text-red-400 hover:text-red-600" />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => openTransferDialog(m, dept)} title="Transférer" data-testid={`transfer-member-${m.id}`}>
                                            <ArrowRightLeft className="w-3.5 h-3.5 text-gray-500" />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </>
    );
}

/* =============== REMOVE MEMBER DIALOG =============== */
function RemoveMemberDialog({ open, onOpenChange, targetUsers, dept, users, onConfirm }) {
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const isBulk = targetUsers.length > 1;
    const singleUser = targetUsers[0];

    const isManager = singleUser && dept?.manager_id === singleUser.id;
    const isAssistant = singleUser && (dept?.assistant_ids || []).includes(singleUser.id);
    const isPrimary = singleUser?.membership_type === "primary";
    const hasSecondary = (singleUser?.secondary_department_ids || []).length > 0;
    const hasPrimaryOnly = isPrimary && !hasSecondary;

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            await onConfirm(targetUsers.map((u) => u.id), reason);
            onOpenChange(false);
            setReason("");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle data-testid="remove-member-dialog-title">
                        {isBulk
                            ? `Retirer ${targetUsers.length} utilisateur(s) du département ${dept?.name}`
                            : `Retirer ${singleUser?.first_name} ${singleUser?.last_name}`}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 py-3" data-testid="remove-member-dialog">
                    <p className="text-sm text-gray-600">
                        {isBulk
                            ? `Retirer ${targetUsers.length} utilisateur(s) du département ${dept?.name} ?`
                            : `Retirer ${singleUser?.first_name} ${singleUser?.last_name} du département ${dept?.name} ?`}
                    </p>
                    <p className="text-xs text-gray-500">
                        Cette action empêchera la synchronisation Microsoft 365 de remettre automatiquement {isBulk ? "ces utilisateurs" : "cet utilisateur"} dans ce département.
                    </p>

                    {!isBulk && isManager && (
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="warning-manager">
                            <p className="text-xs text-amber-800 font-medium">
                                Cet utilisateur est le responsable de ce département. Pensez à définir un nouveau responsable.
                            </p>
                        </div>
                    )}
                    {!isBulk && isAssistant && (
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="warning-assistant">
                            <p className="text-xs text-amber-800 font-medium">
                                Cet utilisateur est assistante de ce département. Il/elle sera automatiquement retiré(e) des assistantes.
                            </p>
                        </div>
                    )}
                    {!isBulk && isPrimary && (
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3" data-testid="warning-primary">
                            <p className="text-xs text-blue-800 font-medium">
                                {hasPrimaryOnly
                                    ? "Attention : cet utilisateur n'aura plus aucun département."
                                    : "C'est le département principal. Son prochain département secondaire deviendra son département principal."}
                            </p>
                        </div>
                    )}

                    <div>
                        <Label className="form-label text-xs">Motif (optionnel)</Label>
                        <Textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Raison du retrait..."
                            rows={2}
                            data-testid="remove-reason"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                    <Button variant="destructive" onClick={handleConfirm} disabled={submitting} data-testid="confirm-remove-btn">
                        {submitting ? "Retrait..." : "Retirer du département"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* =============== MAIN COMPONENT =============== */
export function AdminDepartments() {
    const { departments, users, currentUser, refreshDepartments, refreshUsers } = useApp();
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [expandedDept, setExpandedDept] = useState(null);
    const [memberData, setMemberData] = useState({});
    const [memberCounts, setMemberCounts] = useState({});
    const [positions, setPositions] = useState([]);
    const [selectedMemberIds, setSelectedMemberIds] = useState(new Set());
    const [bulkDropdown, setBulkDropdown] = useState(null);
    const [formData, setFormData] = useState({
        name: "", manager_id: "", parent_id: "", assistant_ids: [],
    });

    // Transfer dialog state
    const [transferOpen, setTransferOpen] = useState(false);
    const [transferUser, setTransferUser] = useState(null);
    const [transferFromDept, setTransferFromDept] = useState(null);
    const [transferToDeptId, setTransferToDeptId] = useState("");
    const [transferToDeptName, setTransferToDeptName] = useState("");
    const [transferReason, setTransferReason] = useState("");
    const [transferDate, setTransferDate] = useState(new Date().toISOString().slice(0, 10));
    const [transferFreeText, setTransferFreeText] = useState(false);
    const [transferSubmitting, setTransferSubmitting] = useState(false);

    // Remove member dialog state
    const [removeOpen, setRemoveOpen] = useState(false);
    const [removeTargetUsers, setRemoveTargetUsers] = useState([]);
    const [removeTargetDept, setRemoveTargetDept] = useState(null);

    const loadMemberCounts = useCallback(async () => {
        const counts = {};
        for (const dept of departments) {
            try {
                const data = await api.getDepartmentMembers(dept.id);
                counts[dept.id] = data.total;
            } catch {
                counts[dept.id] = 0;
            }
        }
        setMemberCounts(counts);
    }, [departments]);

    useEffect(() => {
        if (departments.length > 0) loadMemberCounts();
    }, [departments, loadMemberCounts]);

    useEffect(() => { api.getPositions().then(setPositions).catch(() => {}); }, []);

    // Clear selection when expanding a different dept
    useEffect(() => { setSelectedMemberIds(new Set()); setBulkDropdown(null); }, [expandedDept]);

    const refreshMembers = useCallback(async (deptId) => {
        refreshUsers();
        const data = await api.getDepartmentMembers(deptId);
        setMemberData((prev) => ({ ...prev, [deptId]: data.members }));
        setMemberCounts((prev) => ({ ...prev, [deptId]: data.total }));
    }, [refreshUsers]);

    const toggleExpand = async (deptId) => {
        if (expandedDept === deptId) {
            setExpandedDept(null);
            return;
        }
        setExpandedDept(deptId);
        if (!memberData[deptId]) {
            try {
                const data = await api.getDepartmentMembers(deptId);
                setMemberData((prev) => ({ ...prev, [deptId]: data.members }));
            } catch {
                toast.error("Erreur lors du chargement des membres");
            }
        }
    };

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditing(item);
            setFormData({
                name: item.name,
                manager_id: item.manager_id || "",
                parent_id: item.parent_id || "",
                assistant_ids: item.assistant_ids || [],
            });
        } else {
            setEditing(null);
            setFormData({ name: "", manager_id: "", parent_id: "", assistant_ids: [] });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            const payload = { ...formData };
            if (!payload.manager_id) payload.manager_id = null;
            if (!payload.parent_id) payload.parent_id = null;
            if (editing) {
                await api.updateDepartment(editing.id, payload);
                toast.success("Département mis à jour");
            } else {
                await api.createDepartment(payload);
                toast.success("Département créé");
            }
            setDialogOpen(false);
            refreshDepartments();
            setTimeout(loadMemberCounts, 500);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer ce département ?")) return;
        try {
            await api.deleteDepartment(id);
            toast.success("Département supprimé");
            refreshDepartments();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
        }
    };

    // --- Assistants management ---
    const handleAddAssistant = (userId) => {
        if (!userId || formData.assistant_ids.includes(userId)) return;
        setFormData({ ...formData, assistant_ids: [...formData.assistant_ids, userId] });
    };

    const handleRemoveAssistant = (userId) => {
        setFormData({ ...formData, assistant_ids: formData.assistant_ids.filter((id) => id !== userId) });
    };

    // --- Transfer ---
    const openTransferDialog = (member, dept) => {
        setTransferUser(member);
        setTransferFromDept(dept);
        setTransferToDeptId("");
        setTransferToDeptName("");
        setTransferReason("");
        setTransferDate(new Date().toISOString().slice(0, 10));
        setTransferFreeText(false);
        setTransferOpen(true);
    };

    const handleTransferSubmit = async () => {
        if (!transferUser || !transferToDeptName) return;
        setTransferSubmitting(true);
        try {
            const empName = `${transferUser.first_name} ${transferUser.last_name}`;
            const fromName = transferFromDept?.name || "N/A";
            const result = await api.createTicket({
                type: "department_transfer",
                title: `Transfert de département — ${empName} : ${fromName} → ${transferToDeptName}`,
                description: `Demande de transfert de ${empName} du département ${fromName} vers ${transferToDeptName}.${transferReason ? `\nMotif : ${transferReason}.` : ""}\nDate effective : ${transferDate}.`,
                priority: "medium",
                requester_id: currentUser.id,
                beneficiary_id: transferUser.id,
                dynamic_fields: {
                    employee_id: transferUser.id,
                    employee_name: empName,
                    from_department_id: transferFromDept?.id || "",
                    from_department_name: fromName,
                    to_department_id: transferToDeptId,
                    to_department_name: transferToDeptName,
                    transfer_reason: transferReason,
                    effective_date: transferDate,
                },
            });
            toast.success(`Ticket de transfert créé : #${result.ticket_number}`);
            setTransferOpen(false);
            navigate(`/tickets/${result.id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de la création du ticket");
        } finally {
            setTransferSubmitting(false);
        }
    };

    const handleSelectTransferDept = (deptId) => {
        if (!deptId || deptId === "none") {
            setTransferToDeptId("");
            setTransferToDeptName("");
            return;
        }
        const dept = departments.find((d) => d.id === deptId);
        setTransferToDeptId(dept?.id || "");
        setTransferToDeptName(dept?.name || "");
    };

    // --- Remove member ---
    const openRemoveDialog = (targetUsersArray, dept) => {
        setRemoveTargetUsers(targetUsersArray);
        setRemoveTargetDept(dept);
        setRemoveOpen(true);
    };

    const handleRemoveConfirm = async (userIds, reason) => {
        const deptId = removeTargetDept?.id;
        if (!deptId) return;
        try {
            if (userIds.length === 1) {
                await api.removeDepartmentMember(deptId, { user_id: userIds[0], reason });
                const u = removeTargetUsers[0];
                toast.success(`${u.first_name} ${u.last_name} retiré(e) du département ${removeTargetDept.name}`);
            } else {
                const result = await api.bulkRemoveDepartmentMembers(deptId, { user_ids: userIds, reason });
                toast.success(`${result.removed} utilisateur(s) retiré(s) du département ${removeTargetDept.name}`);
            }
            setSelectedMemberIds(new Set());
            await refreshMembers(deptId);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors du retrait");
        }
    };

    return (
        <div className="page-container" data-testid="admin-departments">
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Départements</h1>
                    <p className="page-subtitle">{departments.length} départements</p>
                </div>
                <Button onClick={() => handleOpenDialog()} data-testid="add-department-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                </Button>
            </div>

            {departments.length === 0 ? (
                <div className="card">
                    <EmptyState icon={Building2} title="Aucun département" description="Ajoutez des départements à votre organisation" />
                </div>
            ) : (
                <div className="card">
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Membres</th>
                                    <th>Responsable</th>
                                    <th>Assistante(s)</th>
                                    <th>Parent</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {departments.map((dept) => {
                                    const manager = users.find((u) => u.id === dept.manager_id);
                                    const parent = departments.find((d) => d.id === dept.parent_id);
                                    const assistants = (dept.assistant_ids || []).map((id) => users.find((u) => u.id === id)).filter(Boolean);
                                    const count = memberCounts[dept.id] ?? "...";
                                    const isExpanded = expandedDept === dept.id;
                                    const members = memberData[dept.id] || [];

                                    return (
                                        <React.Fragment key={dept.id}>
                                            <tr data-testid={`department-row-${dept.id}`}>
                                                <td className="font-medium">{dept.name}</td>
                                                <td>
                                                    <button
                                                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                                                        onClick={() => toggleExpand(dept.id)}
                                                        data-testid={`dept-members-btn-${dept.id}`}
                                                    >
                                                        <Users className="w-3 h-3" />
                                                        {count} membre{count !== 1 ? "s" : ""}
                                                        {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                                    </button>
                                                </td>
                                                <td>{manager ? `${manager.first_name} ${manager.last_name}` : "—"}</td>
                                                <td>
                                                    {assistants.length > 0 ? (
                                                        <div className="flex flex-wrap gap-1">
                                                            {assistants.map((a) => (
                                                                <span key={a.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700" data-testid={`assistant-badge-${a.id}`}>
                                                                    {a.first_name} {a.last_name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-300">—</span>
                                                    )}
                                                </td>
                                                <td>{parent?.name || "—"}</td>
                                                <td>
                                                    <div className="flex items-center gap-2 justify-end">
                                                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(dept)} data-testid={`edit-dept-${dept.id}`}>
                                                            <Pencil className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={() => handleDelete(dept.id)} data-testid={`delete-dept-${dept.id}`}>
                                                            <Trash2 className="w-4 h-4 text-red-500" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr data-testid={`dept-members-panel-${dept.id}`}>
                                                    <td colSpan={6} className="!p-0">
                                                        <div className="bg-gray-50 border-t border-b px-6 py-3">
                                                            {members.length === 0 ? (
                                                                <p className="text-sm text-gray-400 italic">Aucun membre</p>
                                                            ) : (
                                                                <MembersPanel
                                                                    members={members}
                                                                    dept={dept}
                                                                    positions={positions}
                                                                    selectedMemberIds={selectedMemberIds}
                                                                    setSelectedMemberIds={setSelectedMemberIds}
                                                                    bulkDropdown={bulkDropdown}
                                                                    setBulkDropdown={setBulkDropdown}
                                                                    openTransferDialog={openTransferDialog}
                                                                    onRefresh={() => refreshMembers(dept.id)}
                                                                    onRemoveMember={(usrs, d) => openRemoveDialog(usrs, d)}
                                                                />
                                                            )}
                                                            <ExclusionsPanel deptId={dept.id} deptName={dept.name} />
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* EDIT/CREATE DEPARTMENT DIALOG */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Modifier le département" : "Ajouter un département"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="form-label">Nom *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Ex: Ressources Humaines"
                                data-testid="department-name"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Responsable</Label>
                            <UserSearchSelect
                                users={users}
                                departments={departments}
                                value={formData.manager_id || null}
                                onChange={(id) => {
                                    // Remove from assistants if becoming manager
                                    const newAssistants = (formData.assistant_ids || []).filter((a) => a !== id);
                                    setFormData({ ...formData, manager_id: id || "", assistant_ids: newAssistants });
                                }}
                                placeholder="Rechercher un responsable..."
                                data-testid="department-manager"
                            />
                        </div>
                        {/* Assistants section */}
                        <div>
                            <Label className="form-label">Assistante(s)</Label>
                            {formData.assistant_ids.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-2" data-testid="assistant-list">
                                    {formData.assistant_ids.map((aid) => {
                                        const u = users.find((x) => x.id === aid);
                                        return (
                                            <span key={aid} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                                                {u ? `${u.first_name} ${u.last_name}` : aid}
                                                <button type="button" onClick={() => handleRemoveAssistant(aid)} className="hover:text-red-500" data-testid={`remove-assistant-${aid}`}>
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                            <UserSearchSelect
                                users={users.filter((u) => u.is_active !== false)}
                                departments={departments}
                                value={null}
                                onChange={handleAddAssistant}
                                placeholder="Ajouter une assistante..."
                                excludeUserIds={[formData.manager_id, ...formData.assistant_ids].filter(Boolean)}
                                data-testid="add-assistant-search"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Département parent</Label>
                            <Select
                                value={formData.parent_id}
                                onValueChange={(v) => setFormData({ ...formData, parent_id: v })}
                            >
                                <SelectTrigger data-testid="department-parent">
                                    <SelectValue placeholder="Aucun (racine)" />
                                </SelectTrigger>
                                <SelectContent>
                                    {departments.filter((d) => d.id !== editing?.id).map((d) => (
                                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                        <Button onClick={handleSave} disabled={!formData.name} data-testid="save-department-btn">
                            {editing ? "Enregistrer" : "Créer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* TRANSFER DIALOG */}
            <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Transférer de département</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4" data-testid="transfer-dialog">
                        <div>
                            <Label className="form-label text-gray-500">Collaborateur</Label>
                            <p className="text-sm font-medium">{transferUser?.first_name} {transferUser?.last_name}</p>
                        </div>
                        <div>
                            <Label className="form-label text-gray-500">Département actuel</Label>
                            <p className="text-sm">{transferFromDept?.name || "—"}</p>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <Label className="form-label">Nouveau département *</Label>
                                <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                                    <input type="checkbox" checked={transferFreeText} onChange={(e) => { setTransferFreeText(e.target.checked); setTransferToDeptId(""); setTransferToDeptName(""); }} className="w-3 h-3" />
                                    Saisie libre
                                </label>
                            </div>
                            {transferFreeText ? (
                                <Input
                                    value={transferToDeptName}
                                    onChange={(e) => { setTransferToDeptName(e.target.value); setTransferToDeptId(""); }}
                                    placeholder="Nom du département..."
                                    data-testid="transfer-to-dept-free"
                                />
                            ) : (
                                <Select value={transferToDeptId || "none"} onValueChange={handleSelectTransferDept}>
                                    <SelectTrigger data-testid="transfer-to-dept-select">
                                        <SelectValue placeholder="Sélectionner..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Sélectionner...</SelectItem>
                                        {departments.filter((d) => d.id !== transferFromDept?.id).map((d) => (
                                            <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                        <div>
                            <Label className="form-label">Motif</Label>
                            <Textarea value={transferReason} onChange={(e) => setTransferReason(e.target.value)} rows={2} placeholder="Réorganisation, promotion..." data-testid="transfer-reason" />
                        </div>
                        <div>
                            <Label className="form-label">Date effective</Label>
                            <Input type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} data-testid="transfer-date" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTransferOpen(false)}>Annuler</Button>
                        <Button onClick={handleTransferSubmit} disabled={transferSubmitting || !transferToDeptName} data-testid="confirm-transfer-btn">
                            {transferSubmitting ? "Création..." : "Confirmer le transfert"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* REMOVE MEMBER DIALOG */}
            <RemoveMemberDialog
                open={removeOpen}
                onOpenChange={setRemoveOpen}
                targetUsers={removeTargetUsers}
                dept={removeTargetDept}
                users={users}
                onConfirm={handleRemoveConfirm}
            />
        </div>
    );
}
